import { ProcessPartnerTypes } from './process-partner-types.enum';
import { ICreateProcessPartner } from './process-partners.models';

type ProcessPartnerRequestField = keyof ICreateProcessPartner;

const PAYMENT_SYSTEM_FIELDS: readonly ProcessPartnerRequestField[] = [
  'paymentCustomerNumber',
  'disableBordereauStage',
  'paymentTargetCountries',
];

const DEALER_ONLY_FIELDS: readonly ProcessPartnerRequestField[] = [
  'globalDealerNumber',
  'estimateRequired',
  'processLevelType',
  'currency',
];

const DEALER_UNSUPPORTED_FIELDS: readonly ProcessPartnerRequestField[] = [
  'supplierNumber',
  'isNewLiabilityReportClaim',
  'claimSharingAgreements',
  'paymentTargetCountries',
  'timeLimitDamageRequests',
  'timeLimitRecoveryRequest',
  'timeLimitSettlementRequest',
];

const UNSUPPORTED_FIELDS: Readonly<
  Partial<Record<ProcessPartnerTypes, ReadonlySet<ProcessPartnerRequestField>>>
> = {
  [ProcessPartnerTypes.CLAIM_HANDLER]: new Set([
    ...PAYMENT_SYSTEM_FIELDS,
    ...DEALER_ONLY_FIELDS,
    'isNewLiabilityReportClaim',
    'claimSharingAgreements',
    'tax',
    'timeLimitDamageRequests',
  ] as const),
  [ProcessPartnerTypes.BEARER_OF_RISK]: new Set([
    ...PAYMENT_SYSTEM_FIELDS,
    ...DEALER_ONLY_FIELDS,
    'isNewLiabilityReportClaim',
    'claimSharingAgreements',
    'tax',
    'timeLimitDamageRequests',
    'timeLimitRecoveryRequest',
  ] as const),
  [ProcessPartnerTypes.SURVEYOR]: new Set([
    ...PAYMENT_SYSTEM_FIELDS,
    ...DEALER_ONLY_FIELDS,
    'isNewLiabilityReportClaim',
    'claimSharingAgreements',
    'timeLimitDamageRequests',
    'timeLimitRecoveryRequest',
    'timeLimitSettlementRequest',
  ] as const),
  [ProcessPartnerTypes.CARRIER]: new Set([
    ...DEALER_ONLY_FIELDS,
    'tax',
    'timeLimitSettlementRequest',
  ] as const),
  [ProcessPartnerTypes.PLANT]: new Set([
    ...PAYMENT_SYSTEM_FIELDS,
    ...DEALER_ONLY_FIELDS,
    'tax',
    'timeLimitSettlementRequest',
  ] as const),
  [ProcessPartnerTypes.DEALER]: new Set(DEALER_UNSUPPORTED_FIELDS),
  [ProcessPartnerTypes.GC_AGENT]: new Set([
    ...DEALER_UNSUPPORTED_FIELDS,
    'disableBordereauStage',
  ] as const),
};

export const pruneUnsupportedProcessPartnerFields = (
  request: ICreateProcessPartner
): Partial<ICreateProcessPartner> => {
  const unsupported = UNSUPPORTED_FIELDS[request.type];

  if (unsupported === undefined) {
    return { ...request };
  }

  return Object.fromEntries(
    Object.entries(request).filter(
      ([field]) => !unsupported.has(field as ProcessPartnerRequestField)
    )
  ) as Partial<ICreateProcessPartner>;
};
